import React from 'react';
import { Box } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chart.js/auto';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const baseColors = ["#305CCB", "#7c91a2", "#A33600", "#d59084", "#0C141D", "#E0C200", "#ff992b", "#ffde19", "#FFADAD"];

const StackedBarVisualization = ({ data, indicators, stateName }) => {
  const months = [...new Set(data.map(item => new Date(item.Month).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })))];

  const excludedLabels = ["Total Members", "Social Following"];
  const relevantIndicators = indicators.filter(
    indicator => !excludedLabels.includes(indicator.label)
  );
  const chartData = {
    labels: relevantIndicators.map(indicator => {
      if (indicator.label == 'Media Reach') {
        return 'Media Reach (in millions)'
      } else {
        return indicator.label
      }
    }),
    datasets: months.map((month, index) => ({
      label: month,
      data: relevantIndicators.map(indicator => {
        const item = data.find(d => new Date(d.Month).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) === month && d[indicator.label] !== '-');
        
        // 2024-11-13: to ensure the various data series can be visualized together, we divide
        // the Media Reach data by a million so they are on a similar scale as the other series
        if (item) {
          let item_value = item[indicator.label]
          if (indicator.label == 'Media Reach') {
            item_value = parseFloat((item_value / 1000000).toFixed(2))
          }
          return item_value
          } else {
            return 0;
          }
      }),
      backgroundColor: hexToRgba(baseColors[index % baseColors.length], 0.4),
      borderColor: baseColors[index % baseColors.length],
      borderWidth: 2,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    layout: {
      padding: {
          left: 10,
          right: 50,
          top: 0,
          bottom: 0
      }
  },
    plugins: {
      legend: {
        position: 'left',
        title: {
          display: true,
          text: 'Click to add/remove',
          padding: 10,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: true,
        callbacks: {
          label: function(tooltipItem) {
            const dataset = chartData.datasets[tooltipItem.datasetIndex];
            const value = dataset.data[tooltipItem.dataIndex];
            const formattedValue = new Intl.NumberFormat().format(value); // Format item with thousands separator
            return `${dataset.label}: ${formattedValue}`;
          },
        }
      },
      datalabels: {
        display: true,
        clamp: true,
        anchor: 'end',
        align: 'end',
        formatter: (value, context) => {
          const datasetArray = [];
          const legendItems = context.chart.legend.legendItems;
          context.chart.data.datasets.forEach((dataset, datasetIndex) => {
            const legendItem = legendItems[datasetIndex];
            if (!legendItem.hidden && dataset.data[context.dataIndex] !== undefined) {
              datasetArray.push(dataset.data[context.dataIndex]);
            }
          });
          const totalSum = datasetArray.reduce((total, datapoint) => total + datapoint, 0);
          let lastVisibleIndex = -1;
          for (let i = legendItems.length - 1; i >= 0; i--) {
            if (!legendItems[i].hidden) {
              lastVisibleIndex = i;
              break;
            }
          }

          if (context.datasetIndex === lastVisibleIndex) {
            const formattedTotalSum = new Intl.NumberFormat().format(totalSum); // Format item with thousands separator
            return formattedTotalSum;
          } else {
            return '';
          }
        },
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  return (
    <Box className="stacked-bar-graph-container">
      <Bar data={chartData} options={options}/>
    </Box>
  );
};

export default StackedBarVisualization;
